import React, { useState, useEffect, useMemo } from 'react';
import { motion, Reorder } from 'framer-motion';
import { GripVertical, X, Save, Undo2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { FavoriteUser } from '@/hooks/use-favorite-users';
import { useFavoriteUserMutations } from '@/hooks/use-favorite-user-mutations';

interface DraggableUserListProps {
  favorites: FavoriteUser[];
}

const DraggableUserList = ({ favorites }: DraggableUserListProps) => {
  const [items, setItems] = useState(favorites);
  const { removeFavorite, batchReorderFavorites } = useFavoriteUserMutations();

  // Sync local state when favorites prop changes
  useEffect(() => {
    setItems(favorites);
  }, [favorites]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (items.length !== favorites.length) return true;
    return items.some((item, index) => {
      const originalItem = favorites.find(fav => fav.id === item.id);
      return !originalItem || originalItem.position !== index + 1;
    });
  }, [items, favorites]);

  const handleReorder = (newOrder: FavoriteUser[]) => {
    console.log('Reordering:', newOrder.map(item => ({ id: item.id, username: item.profile.username })));
    setItems(newOrder);
  };

  const handleSaveChanges = () => {
    // Simply pass the current ordered items to the mutation
    batchReorderFavorites.mutate(items);
  };

  const handleCancelChanges = () => {
    setItems(favorites);
  };

  const handleRemove = (favorite: FavoriteUser) => {
    removeFavorite.mutate({
      favoriteId: favorite.id,
      position: favorite.position
    });
  };

  if (favorites.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No favorites added yet</p>
        <p className="text-sm text-muted-foreground">Switch to "Add Users" tab to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasChanges && (
        <div className="flex items-center justify-between p-3 bg-muted/50 border border-border rounded-lg">
          <p className="text-sm text-muted-foreground">
            You have unsaved changes to your favorites order
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelChanges}
              disabled={batchReorderFavorites.isPending}
            >
              <Undo2 className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveChanges}
              disabled={batchReorderFavorites.isPending}
            >
              <Save className="w-4 h-4 mr-1" />
              Save Changes
            </Button>
          </div>
        </div>
      )}

      <div className="max-h-96 overflow-y-auto pr-2">
        <Reorder.Group
          axis="y"
          values={items}
          onReorder={handleReorder}
          className="space-y-2"
        >
          {items.map((favorite, index) => (
            <Reorder.Item
              key={favorite.id}
              value={favorite}
              className="group"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors cursor-grab active:cursor-grabbing"
              >
                {/* Drag Handle */}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <GripVertical className="w-4 h-4" />
                  <span className="text-sm font-bold w-6 text-center">
                    #{index + 1}
                  </span>
                </div>

                {/* User Info */}
                <Avatar className="w-10 h-10">
                  <AvatarImage src={favorite.profile.profile_image_url || undefined} />
                  <AvatarFallback>
                    {favorite.profile.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {favorite.profile.display_name || `@${favorite.profile.username}`}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    @{favorite.profile.username}
                  </p>
                </div>

                {/* Recent Post Indicator */}
                <div className="flex items-center gap-2">
                  {favorite.recent_bulletin ? (
                    <div className="w-2 h-2 bg-green-500 rounded-full" title="Posted in last 24h" />
                  ) : (
                    <div className="w-2 h-2 bg-muted-foreground/30 rounded-full" title="No recent posts" />
                  )}
                </div>

                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(favorite)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={removeFavorite.isPending}
                >
                  <X className="w-4 h-4" />
                </Button>
              </motion.div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </div>
    </div>
  );
};

export default DraggableUserList;